import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { frontalFitScale, perspectiveArtworkQuad, Point, quadMatrix3d } from './wall-composition.util';

/** Środek pracy en face (render bez czworokąta) leży na tym ułamku wysokości obrazu renderu. */
const FRONTAL_CENTER_Y_RATIO = 0.4;

export interface WallInterior {
  id: number;
  name: string;
  imageUrl: string | null;
  pxPerCm: number | null;
  quad: { x: number; y: number }[] | null;
  shotType: string | null;
  minSizeCm: number | null;
  maxSizeCm: number | null;
}

/**
 * Silnik kompozycji (FE-1). Nakłada wycinek pracy na render wnętrza w realnej skali:
 * mierzy natywny rozmiar renderu, przelicza px/cm na wymiar wyświetlany, orientuje pracę
 * wg czworokąta strefy zawieszenia. En face = dokładny (prostokąt wyśrodkowany na
 * czworokącie). Perspektywa = rzut projekcyjny (homografia) czworokąta — praca dziedziczy
 * zbieżność renderu (zwęża się ku dalszej krawędzi); dokładność metryczna głębi/kalibracji
 * punktu zawieszenia = weryfikacja na realnym renderze / PoC — patrz AT-9082). Bez WebGL.
 * Browser-only (SSR: nic nie liczymy).
 */
@Component({
  selector: 'app-wall-composition',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wall-composition.component.html',
  styleUrls: ['./wall-composition.component.scss'],
})
export class WallCompositionComponent implements AfterViewInit, OnChanges {
  @Input() interior!: WallInterior;
  @Input() dimensions: { width: number; height: number } | null = null;

  /**
   * Klasy stylów snippetu „Render" (uId + m{modelId}). Puste = blok ze starszej fabryki,
   * bez snippetu w scenie; wtedy obraz dostaje wymiary domyślne (AT-9303, zwrotka 21.09).
   */
  @Input() imageClasses: string[] = [];
  /**
   * Autor nie ustawił w kreatorze wymiarów snippetu „Render" — obraz dostaje wymiary domyślne
   * (wpisanie w scenę). Gdy ustawił, żadne ograniczenie z komponentu nie może ich przycinać
   * (AT-9456 #3: render na pełną szerokość/wysokość okna).
   */
  @Input() defaultSize = true;
  @Input() cutoutPath: string | null = null;
  // Bramka ujawnienia wycinka (fly-in): dopóki false, `.wc__artwork` renderuje się z opacity 0
  // (jest już w docelowej, przekrzywionej pozycji) — ujawniamy na crossfade po zakończeniu lotu.
  @Input() revealArtwork = false;

  @ViewChild('interiorImg') interiorImg?: ElementRef<HTMLImageElement>;
  @ViewChild('wc') wcRef?: ElementRef<HTMLElement>;

  // Emit po policzeniu kompozycji (obraz wnętrza załadowany, dst gotowy) — dyrygent (wall-preview)
  // odpala wtedy animację fly-in.
  @Output() composed = new EventEmitter<void>();

  artworkTransform = '';
  artworkWidthPx = 0;
  artworkHeightPx = 0;
  // Docelowy czworokąt strefy (4 pkt TL,TR,BR,BL w px wyświetlanych, względem `.wc`) — do fly-in.
  dst: Point[] = [];
  ready = false;

  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    this.recompute();
  }

  ngOnChanges(): void {
    this.recompute();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.recompute();
  }

  onImageLoad(): void {
    this.recompute();
  }

  private recompute(): void {
    if (!this.isBrowser || !this.dimensions || !this.interior?.pxPerCm) {
      return;
    }
    const img = this.interiorImg?.nativeElement;
    if (!img || !img.naturalWidth || !img.clientWidth) {
      return;
    }

    const scale = img.clientWidth / img.naturalWidth; // natywne px renderu -> wyświetlane
    const pxPerCm = this.interior.pxPerCm * scale;
    this.artworkWidthPx = this.dimensions.width * pxPerCm;
    this.artworkHeightPx = this.dimensions.height * pxPerCm;

    const quad = (this.interior.quad ?? []).map((p) => ({ x: p.x * scale, y: p.y * scale }));

    if (quad.length !== 4) {
      // Bez czworokąta praca idzie en face — zmniejszamy ją jednolicie, żeby nie wychodziła poza render.
      const fit = frontalFitScale(
        this.artworkWidthPx, this.artworkHeightPx, img.clientWidth, img.clientHeight, FRONTAL_CENTER_Y_RATIO,
      );
      this.artworkWidthPx *= fit;
      this.artworkHeightPx *= fit;
    }

    const dst = quad.length === 4 ? this.perspectiveQuad(quad, pxPerCm) : this.frontalQuad(img);
    this.dst = dst;

    this.artworkTransform = quadMatrix3d(this.artworkWidthPx, this.artworkHeightPx, dst);
    this.ready = true;
    this.composed.emit();
  }

  /**
   * Osadza pracę w płaszczyźnie ściany zdefiniowanej czworokątem kalibracji przez rzut
   * projekcyjny (homografię) — praca dziedziczy realną perspektywę renderu (zwęża się ku
   * dalszej krawędzi). Rozmiar liczony jako ułamek fizycznej rozpiętości ściany (długości
   * krawędzi czworokąta / pxPerCm), wyśrodkowany w strefie zawieszenia; w środku strefy
   * daje ten sam rozmiar co px/cm, dokładając brakującą zbieżność.
   */
  private perspectiveQuad(quad: Point[], pxPerCm: number): Point[] {
    return perspectiveArtworkQuad(quad, pxPerCm, this.dimensions!);
  }

  /** Brak czworokąta → praca en face, wyśrodkowana na renderze (bez perspektywy). */
  private frontalQuad(img: HTMLImageElement): Point[] {
    const cx = img.clientWidth / 2;
    const cy = img.clientHeight * FRONTAL_CENTER_Y_RATIO;
    const halfW = this.artworkWidthPx / 2;
    const halfH = this.artworkHeightPx / 2;
    return [
      { x: cx - halfW, y: cy - halfH }, // TL
      { x: cx + halfW, y: cy - halfH }, // TR
      { x: cx + halfW, y: cy + halfH }, // BR
      { x: cx - halfW, y: cy + halfH }, // BL
    ];
  }
}
